import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

// icon
import SyncIcon from "@material-ui/icons/Sync";
import UploadIcon from "mdi-material-ui/Upload";
import GetAppIcon from "@material-ui/icons/GetApp";

// jazzasoft
import {
  crudGetList,
  List,
  TextField,
  FunctionField,
  CreateButton,
  EditButton,
  ShowButton,
  Button,
  SelectInput,
  Filter,
  FilterButton,
  DateInput
} from "jazasoft";

import Datagrid from "../../components/Datagrid";
import TooltipTextField from "../../components/TooltipTextField";

import useUnit from "../../hooks/useUnit";
import { useMutation, useQuery, useTenant } from "../../hooks";
import { authServerUrl } from "../../App";
import { getDistinctValues, inputOptions, isEmpty, listFromCsv } from "../../utils/helpers";
import { UsersFields } from "../../utils/entityFields";
import SettingKeys from "../../utils/SettingKeys";
import { useSetting } from "../../hooks";

const choicesEnabled = [
  { id: "true", name: "Active" },
  { id: "false", name: "Not Active" }
];

const parseFilterValues = ({ unitIds, roles, enabled, createdAtFrom, createdAtTo }) => ({
  unitIds: unitIds ? `*${unitIds}*` : undefined,
  roles: roles ? `*${roles}*` : undefined,
  enabled
  // [`createdAt=ge`]: !isEmpty(createdAtFrom) && new Date(createdAtFrom).getTime(),
  // [`createdAt=le`]: !isEmpty(createdAtTo) && new Date(createdAtTo).getTime(),
});

const Actions = ({ basePath, resource, filters, onSync, history, onDownload, ...rest }) => (
  <div>
    <CreateButton basePath={basePath} showLabel={false} />
    <Button showLabel={false} label="Upload" onClick={() => history.push("/users/upload")}>
      <UploadIcon />
    </Button>
    <Button showLabel={false} label="download" onClick={onDownload}>
      <GetAppIcon />
    </Button>
    <Button showLabel={false} label="Sync" onClick={onSync}>
      <SyncIcon />
    </Button>
    <FilterButton showLabel={false} />
  </div>
);

const useStyles = makeStyles(theme => ({
  button: {
    width: theme.spacing(1)
  }
}));

const User = props => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { allUnitList } = useUnit();
  const tenant = useTenant();

  const [roleList, setRoleList] = useState([]);
  const filterValues = useSelector(state => state.form["filter-form"]?.values);

  const { data: apis = {} } = useQuery({ url: "apis", params: { appId: "iam" } });
  const { data: userList = [] } = useQuery({ url: "users", params: { size: 100000 } });
  const { apiKey = "" } = apis[0] || {};

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, "users");
  const [download] = useMutation({ rawResponse: true });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isEmpty(apiKey)) return;
    const fetchRolesAsync = async () => {
      const options = { url: `${authServerUrl}/roles`, method: "get", headers: { "x-api-key": apiKey } };
      const response = await axios.request(options);
      if (response.status === 200) {
        const roles = response.data?.content || [];
        setRoleList(roles.map(role => ({ id: role.roleId, name: role.name })));
      }
    };
    fetchRolesAsync();
  }, [apiKey]);

  const syncUsers = () => {
    dispatch(crudGetList(props.resource, null, null, null, null, { params: { action: "sync" } }));
  };

  const unitChoices = allUnitList.map(({ unitId, name }) => ({ id: unitId, name }));

  const onDownload = () => {
    if (isEmpty(userList)) return [];

    const fileName = "User.xlsx";

    const columnList = [
      { dataKey: "createdAt", title: "Created At", type: "date", cellStyle: { dataFormat: "mmm dd, yy hh:mm AM/PM" } },
      { dataKey: "createdBy", title: "Created By" },

      { dataKey: "modifiedAt", title: "Modified At", type: "date", cellStyle: { dataFormat: "mmm dd, yy hh:mm AM/PM" } },
      { dataKey: "modifiedBy", title: "Modified By" },

      { dataKey: "enabled", title: "Active Status" },
      { dataKey: "deActivatedAt", title: "Deactivated At", type: "date", cellStyle: { dataFormat: "mmm dd, yy hh:mm AM/PM" } },

      { dataKey: "deActivatedBy", title: "Deactivated By" },

      { dataKey: "fullName", title: "Full Name" },
      { dataKey: "username", title: "User Name" },
      { dataKey: "email", title: "Email" },
      { dataKey: "mobile", title: "Mobile" },

      { dataKey: "roles", title: "Roles" },
      { dataKey: "unitIds", title: "Unit IDs" },
      { dataKey: "warehouseIds", title: "Warehouse IDs" },
      { dataKey: "customerIds", title: "Customer IDs" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
    const rowList = userList
      .filter(e => isEmpty(filterValues?.unitIds) || e.unitIds?.split(",")?.includes(filterValues?.unitIds))
      .filter(e => isEmpty(filterValues?.roles) || e.roles?.split(",")?.includes(filterValues?.roles))
      .filter(e => isEmpty(filterValues?.enabled) || (e.enabled ? "true" : "false") === filterValues?.enabled)
      .map(e => ({
        ...e,

        enabled: e.enabled ? "Yes" : "No",

        roles: getDistinctValues(e?.roles?.split(","))?.join(",") || "",
        unitIds: getDistinctValues(e?.unitIds?.split(","))?.join(",") || "",
        warehouseIds: getDistinctValues(e?.warehouseIds?.split(","))?.join(",") || "",
        customerIds: getDistinctValues(e?.customerIds?.split(","))?.join(",") || ""
      }))
      .map((row, idx) => ({
        rowNo: idx + 1,
        cellList: columnList.map(c => ({
          dataKey: c.dataKey,
          value: row[c.dataKey] || ""
        }))
      }));

    const maxColNo = columnList.length;

    const cellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: tenant?.name || "",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      }
      // {
      //   rowNo: 2,
      //   colNo: 1,
      //   value: "User List",
      //   colEnd: maxColNo,
      //   cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
      //   cellHeight: 32,
      // },
    ];

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [{ sheetNo: 0, sheetName: "User", autoSizeColumn: true, excelData: { columnList, rowList, cellList } }],
      params: { filename: fileName }
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var filename = header ? header.match(/filename="(.+)"/)[1] : fileName;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
      }
    });
  };

  return (
    <List
      searchKeys={["fullName", "username", "email", "roles"]}
      actions={<Actions history={props.history} onSync={syncUsers} onDownload={onDownload} />}
      filters={
        <Filter>
          <DateInput
            label={"Created From"}
            source="createdAtFrom"
            xs={6}
            fullWidth={true}
            options={{ fullWidth: true, InputLabelProps: { shrink: true } }}
            {...inputOptions(6)}
          />

          <DateInput
            label={"Created To"}
            source="createdAtTo"
            xs={6}
            fullWidth={true}
            options={{ fullWidth: true, InputLabelProps: { shrink: true } }}
            {...inputOptions(6)}
          />

          <SelectInput source="unitIds" i18nKey={props.i18nKey} choices={unitChoices} {...inputOptions(12)} />
          <SelectInput source="roles" i18nKey={props.i18nKey} choices={roleList} {...inputOptions(12)} />
          <SelectInput source="enabled" i18nKey={props.i18nKey} choices={choicesEnabled} {...inputOptions(12)} />
        </Filter>
      }
      parse={parseFilterValues}
      {...props}>
      <Datagrid>
        {fields.includes(UsersFields.FULL_NAME) && <TextField source="fullName" />}
        {fields.includes(UsersFields.USER_NAME) && <TextField source="username" />}
        {fields.includes(UsersFields.EMAIL) && <TextField source="email" />}
        {fields.includes(UsersFields.ROLES) && <TooltipTextField source="roles" />}
        {allUnitList?.length > 1 && (
          <FunctionField
            source="unitIds"
            render={record =>
              isEmpty(record?.unitIds)
                ? ""
                : allUnitList
                    .filter(u => listFromCsv(record?.unitIds).includes(u.unitId))
                    .map(u => u.name)
                    .join(", ")
            }
          />
        )}
        <ShowButton cellClassName={classes.button} />
        <EditButton cellClassName={classes.button} />
      </Datagrid>
    </List>
  );
};

export default User;
